import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const apiUrl = process.env.REACT_APP_API_URL;

function SortablePhotoItem({ id, photo, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="col-sm-6 col-lg-4 col-xl-3">
      <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ position: 'relative' }}>
          <img
            src={`${apiUrl}/${photo.image_path}`}
            alt={photo.title || 'gallery photo'}
            style={{ width: '100%', height: 160, objectFit: 'cover' }}
          />
          <span
            className="badge bg-main-600 text-white position-absolute rounded-pill text-13 fw-medium"
            style={{ top: 10, left: 10, padding: '4px 12px' }}
          >
            #{photo.position}
          </span>
          <span
            {...attributes}
            {...listeners}
            className="position-absolute bg-white rounded-circle d-flex align-items-center justify-content-center shadow-sm"
            style={{ top: 10, right: 10, width: 32, height: 32, cursor: 'grab' }}
            title="Drag to reorder"
          >
            <i className="ph ph-dots-six-vertical text-gray-500" />
          </span>
        </div>
        <div className="card-body py-12 px-16">
          <span className="fw-semibold text-15 text-truncate d-block mb-10" title={photo.title}>
            {photo.title || 'Untitled'}
          </span>
          <button
            type="button"
            className="btn btn-sm rounded-pill py-6 px-16 w-100"
            style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff', opacity: 1 }}
            onClick={() => onDelete(photo.id)}
          >
            <i className="ph ph-trash me-1" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminPhotoGallery() {
  const [photos, setPhotos] = useState([]);
  const [title, setTitle] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const getToken = () => {
    const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
    return sessionData?.token || null;
  };

  const fetchPhotos = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) { setError('You must be logged in to manage the photo gallery.'); setLoading(false); return; }
      const response = await axios.get(`${apiUrl}/api/admin/photo-gallery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) setPhotos(response.data.photos);
      else setError(response.data.message || 'Failed to fetch photos.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching photos.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setTitle('');
    setImage(null);
    setImagePreview(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!image) { setError('Please choose an image to upload.'); return; }
    setSaving(true);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('image', image);

    try {
      const token = getToken();
      if (!token) { setError('Authentication required.'); setSaving(false); return; }
      const response = await axios.post(`${apiUrl}/api/admin/photo-gallery`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setSuccess('Photo added successfully.');
        resetForm();
        fetchPhotos();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to add photo.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error adding photo.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    try {
      const token = getToken();
      const response = await axios.delete(`${apiUrl}/api/admin/photo-gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) fetchPhotos();
      else setError(response.data.message || 'Failed to delete photo.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting photo.');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setPhotos((items) => {
        const oldIndex = items.findIndex((item) => item.id.toString() === active.id);
        const newIndex = items.findIndex((item) => item.id.toString() === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
          ...item,
          position: index + 1,
        }));
        updatePositions(newOrder);
        return newOrder;
      });
    }
  };

  const updatePositions = async (updatedPhotos) => {
    try {
      const token = getToken();
      await axios.put(`${apiUrl}/api/admin/photo-gallery/order`, updatedPhotos, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      setError('Failed to update photo order.');
      fetchPhotos();
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Photo Gallery</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          <div className="card mb-24">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">Add Photo</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-20">
                  <div className="col-12">
                    <label className="h5 mb-8 fw-semibold font-heading d-block">
                      Image <span className="text-red-500">*</span>
                    </label>
                    <div className="d-flex align-items-center gap-16">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="preview"
                          style={{ width: 100, height: 80, borderRadius: 12, objectFit: 'cover', border: '3px solid #e8f5ee' }}
                        />
                      ) : (
                        <div style={{
                          width: 100, height: 80, borderRadius: 12, background: '#f3f4f6',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: '2px dashed #d1d5db'
                        }}>
                          <i className="ph ph-image text-gray-400" style={{ fontSize: 28 }} />
                        </div>
                      )}
                      <input
                        type="file"
                        className="form-control py-11"
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={saving}
                        style={{ maxWidth: 300 }}
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <label htmlFor="title" className="h5 mb-8 fw-semibold font-heading">Title (optional)</label>
                    <input
                      type="text"
                      id="title"
                      className="form-control py-11"
                      placeholder="e.g. Campus Orientation Day"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <div className="col-12 flex-align justify-content-end gap-8">
                    <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                      {saving ? 'Uploading...' : 'Add Photo'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="card">
            <div className="card-header border-bottom border-gray-100 flex-between">
              <h5 className="mb-0">All Photos ({photos.length})</h5>
              <span className="text-13 text-gray-400">
                <i className="ph ph-arrows-out-cardinal me-1" /> Drag cards to reorder
              </span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : photos.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No photos yet. Add one above.</div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={photos.map((p) => p.id.toString())} strategy={rectSortingStrategy}>
                    <div className="row g-16">
                      {photos.map((p) => (
                        <SortablePhotoItem key={p.id} id={p.id.toString()} photo={p} onDelete={handleDelete} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminPhotoGallery;
